import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

const SearchIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 22L20 20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CalendarIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2V5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 2V5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.5 9.09H20.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Calendar helpers ────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'Januar','Februar','März','April','Mai','Juni',
  'Juli','August','September','Oktober','November','Dezember',
];
const DOW = ['Mo','Di','Mi','Do','Fr','Sa','So'];

function buildMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// ─── Text highlight helper ───────────────────────────────────────────────────

function highlightParts(text, query) {
  if (!query.trim()) return [{ text, highlight: false }];
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part) => ({
    text: part,
    highlight: part.toLowerCase() === query.toLowerCase(),
  }));
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTagLabel(ds) {
  return new Date(ds + 'T12:00:00').toLocaleDateString('de-DE', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
}

// Kurzes Tages-Label für Gruppen-Überschriften: Heute / Gestern / Wochentag + Datum.
function tagKurzLabel(ds) {
  if (!ds) return 'Ohne Datum';
  const heute = new Date(); heute.setHours(0, 0, 0, 0);
  const d = new Date(ds + 'T12:00:00');
  const tag = new Date(d); tag.setHours(0, 0, 0, 0);
  const diff = Math.round((heute - tag) / 86400000);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Gestern';
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Bereits absteigend sortierte Liste tageweise in Gruppen bündeln.
function nachTagGruppieren(liste) {
  const gruppen = [];
  liste.forEach((s) => {
    const key = s.erstellt_am ? s.erstellt_am.slice(0, 10) : '?';
    let g = gruppen[gruppen.length - 1];
    if (!g || g.key !== key) { g = { key, items: [] }; gruppen.push(g); }
    g.items.push(s);
  });
  return gruppen;
}

// ─── SucheView ───────────────────────────────────────────────────────────────

export default function SucheView({ stellen = [], onStelleAuswaehlen }) {
  const [query, setQuery] = useState('');
  const [kalenderOffen, setKalenderOffen] = useState(false);
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [calPos, setCalPos] = useState({ top: 0, right: 0 });
  const inputRef = useRef(null);
  const kalenderRef = useRef(null);
  const kalenderBtnRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close calendar on outside click
  useEffect(() => {
    if (!kalenderOffen) return;
    const handler = (e) => {
      if (
        kalenderRef.current && !kalenderRef.current.contains(e.target) &&
        kalenderBtnRef.current && !kalenderBtnRef.current.contains(e.target)
      ) {
        setKalenderOffen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [kalenderOffen]);

  // Days that have at least one application (for orange dots)
  const bewerbungsTage = useMemo(() => {
    const tage = new Set();
    stellen.forEach(s => {
      if (s.erstellt_am) tage.add(s.erstellt_am.slice(0, 10));
    });
    return tage;
  }, [stellen]);

  // When a selected day is set, jump calendar to that month
  useEffect(() => {
    if (selectedDay) {
      const d = new Date(selectedDay + 'T12:00:00');
      setCalendarDate(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [selectedDay]);

  const sorted = [...stellen].sort((a, b) => {
    const da = a.erstellt_am ? new Date(a.erstellt_am) : new Date(0);
    const db = b.erstellt_am ? new Date(b.erstellt_am) : new Date(0);
    return db - da;
  });

  let filtered = sorted;
  if (query.trim()) {
    const q = query.toLowerCase();
    filtered = filtered.filter((s) =>
      (s.name || s.titel || s.stellenbezeichnung || '').toLowerCase().includes(q)
    );
  }
  if (selectedDay) {
    filtered = filtered.filter((s) => s.erstellt_am?.slice(0, 10) === selectedDay);
  }

  // Calendar grid
  const year  = calendarDate.getFullYear();
  const month = calendarDate.getMonth();
  const cells = buildMonthGrid(year, month);
  const today = todayStr();

  function cellStr(day) {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <span style={styles.superLabel}>Bewerbungen</span>
        <h1 style={styles.heading}>Suchen</h1>

        {/* Search bar + calendar button */}
        <div style={{ position: 'relative', marginBottom: 40 }}>
          <div style={{
            ...styles.inputWrapper,
            marginBottom: 0,
            borderColor: kalenderOffen || selectedDay
              ? 'rgba(255,102,0,0.45)'
              : 'rgba(255,102,0,0.15)',
          }}>
            <span style={styles.inputIcon}>
              <SearchIcon size={20} color="var(--clr-primary, #FF6600)" />
            </span>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Bewerbungen Suchen"
              style={styles.input}
            />
            {query && (
              <button style={styles.clearBtn} onClick={() => setQuery('')}>✕</button>
            )}
            {/* Divider */}
            <div style={styles.calDivider} />
            {/* Calendar toggle button */}
            <button
              ref={kalenderBtnRef}
              style={{
                ...styles.calBtn,
                color: kalenderOffen || selectedDay ? '#FF6600' : '#AAA',
                background: kalenderOffen || selectedDay
                  ? 'rgba(255,102,0,0.08)'
                  : 'transparent',
              }}
              title={selectedDay ? `Filter: ${formatDate(selectedDay)}` : 'Nach Tag filtern'}
              onClick={() => {
                if (!kalenderOffen && kalenderBtnRef.current) {
                  const rect = kalenderBtnRef.current.getBoundingClientRect();
                  setCalPos({ top: rect.bottom + 10, right: window.innerWidth - rect.right });
                }
                setKalenderOffen(v => !v);
              }}
            >
              <CalendarIcon size={18} color={kalenderOffen || selectedDay ? '#FF6600' : '#AAA'} />
              {selectedDay && <span style={styles.calActiveDot} />}
            </button>
          </div>

          {/* Active day filter badge */}
          {selectedDay && (
            <div style={styles.dayBadge}>
              <span style={styles.dayBadgeText}>{formatTagLabel(selectedDay)}</span>
              <button
                style={styles.dayBadgeClear}
                onClick={() => { setSelectedDay(null); setKalenderOffen(false); }}
                title="Filter entfernen"
              >×</button>
            </div>
          )}

          {/* Calendar dropdown — rendered in a portal so overflow:auto on parent doesn't clip it */}
          {kalenderOffen && ReactDOM.createPortal(
            <div ref={kalenderRef} style={{ ...styles.calPanel, position: 'fixed', top: calPos.top, right: calPos.right }}>
              {/* Month header */}
              <div style={styles.calHeader}>
                <button
                  style={styles.calNav}
                  onClick={() => setCalendarDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                >‹</button>
                <span style={styles.calTitle}>{MONTH_NAMES[month]} {year}</span>
                <button
                  style={styles.calNav}
                  onClick={() => setCalendarDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                >›</button>
              </div>

              {/* Day-of-week headers */}
              <div style={styles.calGrid}>
                {DOW.map(d => (
                  <div key={d} style={styles.calDow}>{d}</div>
                ))}
                {cells.map((day, i) => {
                  if (day === null) return <div key={`b-${i}`} style={{ aspectRatio: '1' }} />;
                  const ds = cellStr(day);
                  const isToday    = ds === today;
                  const hasApp     = bewerbungsTage.has(ds);
                  const isSelected = ds === selectedDay;

                  return (
                    <div
                      key={ds}
                      style={{
                        ...styles.calCell,
                        ...(isToday && !isSelected ? styles.calCellToday : {}),
                        ...(isSelected ? styles.calCellSelected : {}),
                        ...(hasApp && !isSelected ? styles.calCellHasApp : {}),
                      }}
                      onClick={() => {
                        setSelectedDay(isSelected ? null : ds);
                        setKalenderOffen(false);
                      }}
                    >
                      <span>{day}</span>
                      {hasApp && (
                        <span style={{
                          ...styles.calDot,
                          background: isSelected ? '#fff' : '#FF6600',
                        }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>,
            document.body
          )}
        </div>

        {/* Results section */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionTitle}>
              {selectedDay ? formatTagLabel(selectedDay) : 'Neuste'}
            </span>
            <span style={styles.sectionCount}>{filtered.length} Einträge</span>
          </div>

          {filtered.length === 0 ? (
            <div style={styles.empty}>
              <SearchIcon size={40} color="#C8C0B0" />
              <p style={styles.emptyText}>
                {selectedDay
                  ? 'Keine Bewerbungen an diesem Tag.'
                  : `Keine Treffer für „${query}"`}
              </p>
            </div>
          ) : (
            nachTagGruppieren(filtered).map((gruppe) => (
              <div key={gruppe.key} style={styles.dayGroup}>
                <div style={styles.dayHeader}>
                  <span style={styles.dayLabel}>{tagKurzLabel(gruppe.key === '?' ? null : gruppe.key)}</span>
                  <span style={styles.dayCount}>{gruppe.items.length}</span>
                </div>
                <ul style={styles.list}>
                  {gruppe.items.map((stelle) => {
                    const titel = stelle.name || stelle.titel || stelle.stellenbezeichnung || 'Unbenannte Stelle';
                    const parts = highlightParts(titel, query);
                    return (
                      <li
                        key={stelle.id}
                        style={styles.item}
                        onClick={() => onStelleAuswaehlen(stelle)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255,188,0,0.05)';
                          e.currentTarget.style.borderColor = 'rgba(255,188,0,0.4)';
                          e.currentTarget.style.borderLeftColor = 'var(--clr-primary)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.10)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'var(--bg-elevated, #FFFCF7)';
                          e.currentTarget.style.borderColor = 'rgba(0,0,0,0.07)';
                          e.currentTarget.style.borderLeftColor = 'var(--clr-primary)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                        }}
                      >
                        <div style={styles.itemLeft}>
                          <div>
                            <div style={styles.itemTitle}>
                              {parts.map((p, i) =>
                                p.highlight ? (
                                  <mark key={i} style={styles.mark}>{p.text}</mark>
                                ) : (
                                  <span key={i}>{p.text}</span>
                                )
                              )}
                            </div>
                            {stelle.unternehmen && (
                              <div style={styles.itemSub}>{stelle.unternehmen}</div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                          {stelle.bewerbungs_status && (
                            <span style={styles.statusBadge(stelle.bewerbungs_status)}>
                              {stelle.bewerbungs_status === 'abgeschickt' ? 'Abgeschickt'
                                : stelle.bewerbungs_status === 'absage' ? 'Absage'
                                : stelle.bewerbungs_status === 'kennenlerngespraech' ? 'Interview'
                                : stelle.bewerbungs_status === 'zusage' ? 'Zusage'
                                : stelle.bewerbungs_status === 'unterlagen_angefertigt' ? 'Unterlagen'
                                : stelle.bewerbungs_status}
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>

      </div>
    </div>
  );
}

const STATUS_BADGE_CONFIG = {
  abgeschickt:            { color: '#0284c7', bg: 'rgba(2,132,199,0.12)' },
  absage:                 { color: '#dc2626', bg: 'rgba(220,38,38,0.12)' },
  kennenlerngespraech:    { color: '#d97706', bg: 'rgba(217,119,6,0.12)' },
  zusage:                 { color: '#059669', bg: 'rgba(5,150,105,0.12)' },
  unterlagen_angefertigt: { color: '#7C6F64', bg: 'rgba(124,111,100,0.12)' },
};

const styles = {
  container: {
    flex: 1,
    overflowY: 'auto',
    background: 'var(--bg-main, #F5F2E9)',
    padding: '48px 40px',
  },
  inner: {
    maxWidth: 680,
    margin: '0 auto',
  },
  superLabel: {
    display: 'block',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--clr-primary, #FFBC00)',
    marginBottom: 8,
  },
  heading: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 36,
    fontWeight: 700,
    color: 'var(--clr-text, #1A1A1A)',
    margin: '0 0 28px 0',
    letterSpacing: '-0.5px',
  },
  sectionTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--clr-text, #1A1A1A)',
    margin: '0 0 16px 0',
    letterSpacing: '-0.3px',
  },
  statusBadge: (status) => {
    const cfg = STATUS_BADGE_CONFIG[status] || { color: '#888', bg: 'rgba(136,136,128,0.12)' };
    return {
      fontSize: 11,
      fontWeight: 700,
      padding: '3px 10px',
      borderRadius: 999,
      color: cfg.color,
      background: cfg.bg,
      whiteSpace: 'nowrap',
      letterSpacing: '0.02em',
    };
  },
  kategoriGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  kategoriCard: (mod) => ({
    borderRadius: 16,
    padding: '24px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    cursor: 'default',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    ...(mod === 'teal' ? {
      background: 'var(--clr-secondary, #004D40)',
    } : mod === 'amber' ? {
      background: 'var(--clr-primary, #FFBC00)',
    } : mod === 'surface2' ? {
      background: 'var(--bg-surface, #FEFCF6)',
      border: '1px solid rgba(0,0,0,0.07)',
    } : {
      background: 'var(--bg-elevated, #FFFFFF)',
      border: '1px solid rgba(0,0,0,0.07)',
    }),
  }),
  kategoriIcon: {
    fontSize: 28,
    marginBottom: 4,
  },
  kategoriLabel: (mod) => ({
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: '-0.3px',
    color: mod === 'teal' ? '#fff' : mod === 'amber' ? 'var(--clr-secondary, #004D40)' : 'var(--clr-text, #1A1A1A)',
  }),
  kategoriSub: (mod) => ({
    fontSize: 12,
    color: mod === 'teal' ? 'rgba(255,255,255,0.55)' : mod === 'amber' ? 'rgba(0,77,64,0.6)' : '#AAA',
  }),
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    background: 'var(--bg-elevated, #FFFCF7)',
    borderRadius: 30,
    boxShadow: '0 4px 20px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)',
    padding: '0 8px 0 20px',
    gap: 12,
    border: '1.5px solid rgba(255,102,0,0.15)',
    transition: 'box-shadow 0.2s, border-color 0.2s',
  },
  inputIcon: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontSize: 15,
    color: 'var(--clr-text, #1A1A1A)',
    padding: '14px 0',
    fontFamily: 'inherit',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#AAA',
    fontSize: 14,
    padding: '4px 6px',
    borderRadius: '50%',
    lineHeight: 1,
    flexShrink: 0,
  },
  calDivider: {
    width: 1,
    height: 22,
    background: 'rgba(0,0,0,0.1)',
    flexShrink: 0,
  },
  calBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    border: 'none',
    borderRadius: 20,
    cursor: 'pointer',
    flexShrink: 0,
    position: 'relative',
    transition: 'background 0.15s, color 0.15s',
  },
  calActiveDot: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#FF6600',
    border: '1.5px solid #FFFCF7',
  },
  dayBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
    padding: '5px 10px 5px 14px',
    background: 'rgba(255,102,0,0.10)',
    border: '1px solid rgba(255,102,0,0.25)',
    borderRadius: 20,
    fontSize: 13,
    color: '#CC4400',
    fontWeight: 600,
  },
  dayBadgeText: {
    lineHeight: 1,
  },
  dayBadgeClear: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#FF6600',
    fontSize: 16,
    lineHeight: 1,
    padding: '0 2px',
    opacity: 0.7,
  },
  calPanel: {
    width: 300,
    background: 'var(--bg-elevated, #FFFCF7)',
    border: '1.5px solid rgba(255,102,0,0.15)',
    borderRadius: 16,
    boxShadow: '0 16px 48px rgba(0,0,0,0.14), 0 4px 12px rgba(0,0,0,0.08)',
    padding: '16px 14px 14px',
    zIndex: 200,
  },
  calHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  calNav: {
    width: 30,
    height: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.04)',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 18,
    color: '#555',
    lineHeight: 1,
    transition: 'background 0.15s',
  },
  calTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--clr-text, #1A1A1A)',
    letterSpacing: '-0.2px',
  },
  calGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 2,
  },
  calDow: {
    textAlign: 'center',
    fontSize: 10,
    fontWeight: 700,
    color: '#AAA',
    paddingBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  calCell: {
    aspectRatio: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    borderRadius: 7,
    cursor: 'pointer',
    color: '#555',
    position: 'relative',
    gap: 2,
    transition: 'background 0.12s',
    userSelect: 'none',
  },
  calCellToday: {
    border: '1.5px solid #FF6600',
    color: '#1A1A1A',
    fontWeight: 700,
  },
  calCellSelected: {
    background: '#FF6600',
    color: '#fff',
    fontWeight: 700,
  },
  calCellHasApp: {
    fontWeight: 600,
    color: '#1A1A1A',
  },
  calDot: {
    width: 4,
    height: 4,
    borderRadius: '50%',
    flexShrink: 0,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 4,
  },
  sectionCount: {
    fontSize: 13,
    color: '#AAA',
    fontWeight: 500,
  },
  dayGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  dayHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 6,
    marginBottom: 2,
  },
  dayLabel: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'capitalize',
    color: 'var(--cv-teal, #004D40)',
  },
  dayCount: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--cv-teal, #004D40)',
    background: 'rgba(0,77,64,0.1)',
    borderRadius: 9999,
    padding: '1px 8px',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--bg-elevated, #FFFCF7)',
    borderRadius: 14,
    padding: '14px 18px',
    cursor: 'pointer',
    border: '1px solid rgba(0,0,0,0.07)',
    borderLeft: '3px solid var(--clr-primary, #FFBC00)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    transition: 'background 0.15s, border-color 0.15s, box-shadow 0.15s',
    userSelect: 'none',
  },
  itemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  itemDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'var(--clr-primary, #FF6600)',
    flexShrink: 0,
  },
  itemTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--clr-text, #1A1A1A)',
    lineHeight: 1.3,
  },
  itemSub: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  itemDate: {
    fontSize: 12,
    color: '#AAA',
    flexShrink: 0,
    marginLeft: 16,
  },
  mark: {
    background: 'rgba(255,102,0,0.18)',
    color: '#CC4400',
    fontWeight: 700,
    borderRadius: 3,
    padding: '0 2px',
    textDecoration: 'none',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: '60px 0',
    color: '#C8C0B0',
  },
  emptyText: {
    fontSize: 15,
    color: '#AAA',
    margin: 0,
  },
};
